/**
 * Formats a total number of minutes into a cumulative "HH:MM" time string.
 * * This function handles invalid values (like `null`, `undefined`, `NaN`, `Infinity`,
 * or negative numbers) by falling back safely to `"00:00"`. Any floating-point
 * decimals provided are truncated using `Math.floor`.
 *
 * @param {number | null | undefined} totalMinutes - The total number of minutes to format.
 * @returns {string} A formatted time string in "HH:MM" format (e.g., "01:30" or "124:15").
 *
 * @example
 * formatMinutesToTimeString(90);        // Returns: "01:30"
 * formatMinutesToTimeString(1440);      // Returns: "24:00" (cumulative hours)
 * formatMinutesToTimeString(50.75);     // Returns: "00:50" (floored decimal)
 * formatMinutesToTimeString(-15);       // Returns: "00:00" (negative fallback)
 * formatMinutesToTimeString(undefined); // Returns: "00:00" (missing value fallback)
 */
export const formatMinutesToTimeString = (totalMinutes: number | null | undefined): string => {
	if (totalMinutes === null || totalMinutes === undefined || Number.isNaN(totalMinutes) || totalMinutes < 0 || totalMinutes === Infinity) {
		return '00:00';
	}

	const totalMinutesInt = Math.floor(totalMinutes);

	const hours = Math.floor(totalMinutesInt / 60);
	const minutes = totalMinutesInt % 60;

	return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
};

/**
 * Formats a total number of seconds into a cumulative "HH:MM" time string.
 * * Internally converts seconds to minutes and leverages {@link formatMinutesToTimeString}
 * to ensure consistent formatting logic. Remainder seconds that do not make up a full
 * minute are truncated.
 *
 * @param {number | null | undefined} totalSeconds - The total number of seconds to format.
 * @returns {string} A formatted time string in "HH:MM" format (e.g., "00:01" or "02:00").
 *
 * @example
 * formatSecondsToTimeString(90);        // Returns: "00:01" (30 remaining seconds are dropped)
 * formatSecondsToTimeString(5400);      // Returns: "01:30"
 * formatSecondsToTimeString(86400);     // Returns: "24:00"
 * formatSecondsToTimeString(null);      // Returns: "00:00" (null fallback)
 */
export const formatSecondsToTimeString = (totalSeconds: number | null | undefined): string => {
	if (totalSeconds === null || totalSeconds === undefined || Number.isNaN(totalSeconds) || totalSeconds < 0 || totalSeconds === Infinity) {
		return '00:00';
	}

	const totalMinutes = Math.floor(totalSeconds / 60);

	return formatMinutesToTimeString(totalMinutes);
};

/**
 * Parses a time string in "HH:MM" format into total seconds.
 * * @param timeStr - The time string to parse (e.g., "01:30").
 * @returns The total seconds, or `null` if the input format is invalid or contains non-numeric values.
 * * @example
 * parseTimeToSeconds("01:30"); // returns 5400
 * parseTimeToSeconds("invalid"); // returns null
 * parseTimeToSeconds("abc:15"); // returns null
 */
export const parseTimeToSeconds = (timeStr: string): number | null => {
	if (!timeStr || !timeStr.includes(':')) return null;

	const parts = timeStr.split(':');
	const hours = Number(parts[0]);
	const minutes = Number(parts[1]);

	if (Number.isNaN(hours) || Number.isNaN(minutes)) return null;

	return hours * 3600 + minutes * 60;
};

/**
 * Parses a time string in "HH:MM" format into total minutes.
 * * @param timeStr - The time string to parse (e.g., "01:30").
 * @returns The total minutes, or `0` if the input format is invalid or contains non-numeric values.
 * * @example
 * parseTimeToMinutes("01:30"); // returns 90
 * parseTimeToMinutes("invalid"); // returns 0
 */
export const parseTimeToMinutes = (timeStr: string): number => {
	if (!timeStr || !timeStr.includes(':')) return 0;

	const parts = timeStr.split(':');
	const hours = Number(parts[0]);
	const minutes = Number(parts[1]);

	if (Number.isNaN(hours) || Number.isNaN(minutes)) return 0;

	return hours * 60 + minutes;
};
